import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { useFeatureFlags } from "@jti/commerce";
import { Separator } from "@jti/core-components/components/Separator";
import type { IMoney, Tax } from "@jti/magento";
import { DEPRECATED_Loading, FormattedPrice, I18n, useTaxesDisplayMode, useTranslations } from "@jti/ui";

import type { SummaryItem } from "@checkout-v2/features/ItemsSummary/ItemsSummaryView/ItemsSummaryView";
import { SummaryLoyaltyPoints } from "@checkout-v2/shared/components/SummaryLoyaltyPoints";

import { PaymentSummaryItemsList } from "../../components/PaymentSummaryItemsList";
import { PaymentSummaryViewHeading } from "../PaymentSummaryViewHeading";
import { SummaryRow } from "../SummaryRow";

import type { PaymentDiscount } from "./PaymentSummaryView.types";

import styles from "./PaymentSummaryView.module.scss";

const { sanitize } = dompurify;

type PaymentSummaryLoyaltyPointsProps = {
  totalLoyaltyPoints: number;
  loyaltyChoice: number;
  learnMoreUrl?: string;
};

export type PaymentSummaryViewProps = {
  title?: string;
  isLoading?: boolean;
  subtotal?: { value: number; currency: string } | IMoney;
  shipping?: { value: number; currency: string } | IMoney;
  total?: { value: number; currency: string } | IMoney;
  appliedTaxes?: Tax[];
  taxValue?: { value: number; currency: string } | IMoney;
  taxMessage?: string;
  discounts?: PaymentDiscount[];
  loyaltyDiscount?: PaymentDiscount;
  loyaltyPoints?: PaymentSummaryLoyaltyPointsProps;
  summaryItems?: SummaryItem[];
  showBackToCartButton?: boolean;
};

export const PaymentSummaryView: FC<PaymentSummaryViewProps> = ({
  title = "",
  isLoading = false,
  subtotal,
  shipping,
  total,
  appliedTaxes,
  taxValue,
  taxMessage,
  discounts,
  loyaltyDiscount,
  loyaltyPoints,
  summaryItems = [],
  showBackToCartButton = false,
}) => {
  const { __ } = useTranslations();
  const taxesDisplaySettings = useTaxesDisplayMode();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const shouldShowBackToCartButton = showBackToCartButton && ffCheckoutV2EnableIteration2;
  const shouldRenderHeading = Boolean(title) || shouldShowBackToCartButton;

  return (
    <div className={styles.summary} data-testid="PaymentSummaryView">
      {shouldRenderHeading && (
        <div className={styles.summaryHeader}>
          <PaymentSummaryViewHeading title={title} showBackToCartButton={shouldShowBackToCartButton} />
        </div>
      )}

      {isLoading ? (
        <DEPRECATED_Loading />
      ) : (
        <div className={styles.summaryContent}>
          {ffCheckoutV2EnableIteration2 && <PaymentSummaryItemsList summaryItems={summaryItems} />}

          <div className={styles.separator}>
            <Separator />
          </div>

          <div className={styles.listing}>
            {subtotal && <SummaryRow label={__("cart.prices.subtotal")} price={subtotal} />}

            {loyaltyDiscount && <SummaryRow label={loyaltyDiscount.label} price={loyaltyDiscount} isDiscount />}

            {discounts?.map((discount) => (
              <SummaryRow key={discount.label} label={discount.label} price={discount} isDiscount />
            ))}

            {shipping && <SummaryRow label={__("cart.prices.shipping")} price={shipping} />}
          </div>

          {taxesDisplaySettings?.showBreakdownWithNames && appliedTaxes && appliedTaxes.length > 0 && (
            <>
              <div className={styles.separator}>
                <Separator />
              </div>
              {appliedTaxes.map((tax) => (
                <SummaryRow
                  key={tax.label}
                  label={`${tax.label} ${tax.rate && `(${tax.rate}%)`}`}
                  price={{ value: tax.amount.value, currency: tax.amount.currency }}
                />
              ))}
            </>
          )}

          <div className={styles.separator}>
            <Separator />
          </div>

          {total && (
            <div className={styles.total}>
              <div className={styles.totalTitle}>
                <div className={styles.totalTitleText}>{__("cart.prices.total.title")}</div>
                <FormattedPrice value={total.value} currency={total.currency} className={styles.totalTitlePrice} />
              </div>
              {taxesDisplaySettings?.showSimpleTotalBreakdown && taxValue && taxValue.value > 0 && (
                <div className={styles.totalSubtitle} data-testid="taxValueInfo">
                  <I18n t="cart.prices.incl_taxes">
                    <FormattedPrice value={taxValue.value} currency={taxValue.currency} />
                  </I18n>
                </div>
              )}
              {taxMessage && (
                <span
                  className={styles.disclaimer}
                  dangerouslySetInnerHTML={{
                    __html: sanitize(taxMessage),
                  }}
                />
              )}
            </div>
          )}

          {loyaltyPoints && (
            <SummaryLoyaltyPoints
              totalLoyaltyPoints={loyaltyPoints.totalLoyaltyPoints}
              loyaltyChoice={loyaltyPoints.loyaltyChoice}
              learnMoreUrl={loyaltyPoints.learnMoreUrl}
            />
          )}
        </div>
      )}
    </div>
  );
};
